import React, { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';

const REASON_LABELS = {
    permissionDenied: 'El navegador no tiene permiso para usar la ubicación.',
    positionUnavailable: 'El teléfono no pudo determinar la ubicación.',
    timeout: 'La ubicación agotó el tiempo de espera configurado.',
    unsupported: 'Este navegador no permite obtener la ubicación.',
    unknown: 'No fue posible validar la lectura del GPS.'
};

const LocationCaptureDialog = ({
    decision,
    onCancel,
    onContinue,
    onRetry
}) => {
    const [note, setNote] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        setNote('');
        setError('');
    }, [decision]);

    const isApproximate = decision?.kind === 'approximate';
    const phaseLabel = decision?.phase === 'finish' ? 'finalizar' : 'iniciar';
    const accuracy = Math.round(Number(decision?.payload?.location?.accuracyMeters || 0));
    const minimumNoteLength = Number(decision?.minimumExceptionNoteLength || 3);
    const locationRequired = Boolean(decision?.required);

    const handleContinue = () => {
        if (!isApproximate && note.trim().length < minimumNoteLength) {
            setError(`Escribe una justificación de al menos ${minimumNoteLength} caracteres.`);
            return;
        }
        onContinue(isApproximate ? null : note.trim());
    };

    const footer = (
        <div className="flex flex-column sm:flex-row gap-2 justify-content-end">
            <Button type="button" label="Cancelar" className="p-button-text" onClick={onCancel} />
            <Button type="button" label="Intentar de nuevo" icon="pi pi-refresh" outlined onClick={onRetry} />
            {isApproximate || !locationRequired ? (
                <Button
                    type="button"
                    label={isApproximate ? `Continuar con ${accuracy} m` : 'Continuar con justificación'}
                    icon={isApproximate ? 'pi pi-map-marker' : 'pi pi-exclamation-triangle'}
                    className={isApproximate ? 'p-button-warning' : 'p-button-danger'}
                    onClick={handleContinue}
                />
            ) : null}
        </div>
    );

    return (
        <Dialog
            className="ventas-dialog"
            header={isApproximate ? 'La ubicación tiene baja precisión' : 'No se pudo obtener la ubicación'}
            visible={Boolean(decision)}
            modal
            closable={false}
            closeOnEscape={false}
            style={{ width: 'min(94vw, 580px)' }}
            footer={footer}
            onHide={onCancel}
        >
            {isApproximate ? (
                <p className="m-0">
                    La lectura tiene una precisión aproximada de <strong>{accuracy} metros</strong>.
                    Puedes volver a intentarlo o continuar dejando registrada esta precisión.
                </p>
            ) : (
                <>
                    <p className="mt-0">
                        {REASON_LABELS[decision?.payload?.locationUnavailableReason] || REASON_LABELS.unknown}
                        {' '}
                        {locationRequired
                            ? ` La configuración exige una ubicación válida para ${phaseLabel} la visita.`
                            : ` Para ${phaseLabel} la visita sin GPS debes explicar el motivo; los usuarios autorizados podrán verlo.`}
                    </p>
                    {!locationRequired ? (
                        <>
                            <label className="block font-medium mb-2" htmlFor="location-exception-note">
                                Justificación
                            </label>
                            <InputTextarea
                                id="location-exception-note"
                                value={note}
                                onChange={(event) => {
                                    setNote(event.target.value);
                                    setError('');
                                }}
                                rows={3}
                                autoResize
                                maxLength={500}
                                className={error ? 'p-invalid w-full' : 'w-full'}
                                placeholder="Ej.: el cliente está dentro de un edificio sin señal GPS"
                                autoFocus
                            />
                            <div className="flex justify-content-between mt-1">
                                <small className={error ? 'p-error' : 'text-color-secondary'}>
                                    {error || 'La justificación quedará guardada con la visita.'}
                                </small>
                                <small className="text-color-secondary">{note.length}/500</small>
                            </div>
                        </>
                    ) : null}
                </>
            )}
        </Dialog>
    );
};

export default LocationCaptureDialog;
